/* eslint-disable react-hooks/rules-of-hooks */
import { useState, useEffect } from "react";
import { Select, Button, Form, Row, Col, DatePicker, Input } from "antd";
import locale from "antd/es/date-picker/locale/es_ES";
import "dayjs/locale/es";
import dayjs from "dayjs";
import calendar from "dayjs/plugin/calendar";

import { formatDate } from "../../utils/utils";
import { useSearchParams } from "react-router-dom";
import {
  useFilters,
  useFetchOcurrencesBy,
} from "../../app/orders/mutations";

dayjs.extend(calendar);

const { RangePicker } = DatePicker;

export function HeaderForm({
  form,
  onSubmit,
  disabled,
  ordersIds,
  documentsIds,
  statuses,
  novedades,
  prioridades,
  responsables,
  fechasCierre,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDisabled, setIsDisabled] = useState(disabled);
  const [_statuses, setStatuses] = useState(statuses);
  const [datePickerDisabled, setDatePickerDisabled] = useState(false);

  const queryParams = Object.fromEntries([...searchParams]);
  const { addFilter } = useFilters();

  useEffect(() => {
    setIsDisabled(disabled);
    setStatuses(statuses);
  }, [disabled, statuses]);

  // Update datePickerDisabled when form initializes or changes
  useEffect(() => {
    const orderValue = form.getFieldValue('numeroOrdenBusqueda');
    setDatePickerDisabled(!!(orderValue && orderValue.trim()));
  }, [form]);

  const onFinish = (values) => {
    const { fechaInicioPedido: f1, numeroOrdenBusqueda } = values;

    let queryParamsToSubmit = {};
    let searchParamsToSet = {};

    if (numeroOrdenBusqueda && numeroOrdenBusqueda.trim() !== "") {
      // Prioritize search by order number
      queryParamsToSubmit = { id_orden: numeroOrdenBusqueda.trim() };
      searchParamsToSet = { id_orden: numeroOrdenBusqueda.trim() };
      form.setFieldsValue({ fechaInicioPedido: null }); // Clear date picker
    } else if (f1 && f1.length === 2) {
      // Proceed with date range search
      queryParamsToSubmit = {
        fechaInicioPedido: formatDate(f1[0].$d),
        fechaFinPedido: formatDate(f1[1].$d),
      };
      searchParamsToSet = { ...queryParamsToSubmit };
    } else {
      // Handle case where neither is provided, or date range is incomplete
      if (!numeroOrdenBusqueda || numeroOrdenBusqueda.trim() === "") {
        form.setFields([{
          name: 'fechaInicioPedido',
          errors: ['Debe ingresar un rango de fechas o un número de orden.'],
        }]);
      }
      if ((!f1 || f1.length !== 2) && (!numeroOrdenBusqueda || numeroOrdenBusqueda.trim() === "")){
        form.setFields([{
          name: 'fechaInicioPedido',
          errors: ['Requerido'],
        }]);
      }
      return;
    }

    setSearchParams(searchParamsToSet);
    onSubmit(queryParamsToSubmit);
    setIsDisabled(false);
    form.setFieldValue("cedula", null);
    form.setFieldValue("fechaFinPromesa", null);
    form.setFieldValue("fechaInicioPromesa", null);
  };

  const handleDocumentChange = (event) => {
    const searchTerm = event;
    if (searchTerm) {
      addFilter({
        label: "Cedula",
        prop: "cedula",
        value: searchTerm,
      });
      useFetchOcurrencesBy();
      form.setFieldValue("cedula", null);
    }
  };

  const handleNovedad = (event) => {
    const searchTerm = event;
    if (searchTerm) {
      addFilter({
        label: "Novedad",
        prop: "novedad",
        value: searchTerm,
      });
      useFetchOcurrencesBy();
    }
    form.setFieldValue("novedad", null);
  };

  const handlePrioridad = (event) => {
    const searchTerm = event;
    if (searchTerm) {
      addFilter({
        label: "Prioridad",
        prop: "prioridad",
        value: searchTerm,
      });
      useFetchOcurrencesBy();
    }
    form.setFieldValue("prioridad", null);
  };

  const handleResponsable = (event) => {
    const searchTerm = event;
    if (searchTerm) {
      addFilter({
        label: "Responsable",
        prop: "responsable",
        value: searchTerm,
      });
      useFetchOcurrencesBy();
    }
    form.setFieldValue("responsable", null);
  };

  const handleFechaCierre = (event) => {
    const searchTerm = event;
    if (searchTerm) {
      addFilter({
        label: "F. Cierre",
        prop: "fechaCierre",
        value: searchTerm,
      });
      useFetchOcurrencesBy();
    }
    form.setFieldValue("fechaCierre", null);
  };

  const handleOrderChange = (event) => {
    const searchTerm = event;
    if (searchTerm && searchTerm.length > 0) {
      addFilter({ label: "Pedido", prop: "pedido", value: searchTerm });
      useFetchOcurrencesBy();
    }
    form.setFieldValue("ordenCompra", null);
  };

  const handleClear = () => {
    form.setFieldValue("nombreCliente", undefined);
  };

  const handleStatusChange = (event) => {
    addFilter({ label: "Estado", prop: "estado", value: event });
    useFetchOcurrencesBy();
    form.setFieldValue("estado", null);
  };

  return (
    <div className="orders-form">
      <Form
        form={form}
        autoComplete="off"
        name="ordersForm"
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          fechaInicioPedido: [
            dayjs(
              queryParams.fechaInicioPedido || dayjs().subtract(2, "day"),
              "YYYY-MM-DD"
            ),
            dayjs(
              queryParams.fechaFinPedido || dayjs().add(2, "day"),
              "YYYY-MM-DD"
            ),
          ],
          numeroOrdenBusqueda: queryParams.id_orden || "",
        }}
      >
        <Row>
          <Col span={24} style={{ textAlign: 'center', marginBottom: 8 }}>
            <b>Buscar por rango de fechas <span style={{ color: '#888' }}>o</span> por identificador de orden</b>
            <div style={{ fontSize: 12, color: '#888' }}>
              Solo uno de los dos campos debe ser completado para realizar la búsqueda. Si ambos tienen valor, se prioriza el identificador de orden.
            </div>
          </Col>
        </Row>
        <Row className="form--first-row" style={{ marginBottom: 15 }}>
          <Col className="gutter-row">
            <Form.Item
              name="fechaInicioPedido"
              label="Fecha Novedad"
              rules={[
                { 
                  required: !form.getFieldValue('numeroOrdenBusqueda'), 
                  message: "Requerido si no se ingresa identificador de orden" 
                }
              ]}
            >
              <RangePicker 
                locale={locale} 
                size="medium" 
                disabled={datePickerDisabled}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name="numeroOrdenBusqueda"
              label="Identificador de Orden"
            >
              <Input 
                placeholder="Ingrese Id Orden" 
                allowClear
                onChange={(e) => {
                  const hasValue = !!e.target.value.trim();
                  setDatePickerDisabled(hasValue);
                  
                  // Clear validation errors on fechaInicioPedido when typing in ID field
                  if (hasValue) {
                    form.setFields([{
                      name: 'fechaInicioPedido',
                      errors: [],
                    }]);
                  }
                  
                  if (!e.target.value.trim()) {
                    form.setFieldsValue({ 
                      fechaInicioPedido: [
                        dayjs(dayjs().subtract(2, "day"), "YYYY-MM-DD"),
                        dayjs(dayjs().add(2, "day"), "YYYY-MM-DD"),
                      ] 
                    });
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item>
              <Button
                htmlType="submit"
                type="default"
                className="btn-green"
                id="btn-order"
                size="medium"
              >
                Consultar
              </Button>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col className="gutter-row">
            <Form.Item
              name="ordenCompra"
              rules={[{ required: false, message: "requerido" }]}
              label="Pedido / Orden"
            >
              <Select
                showSearch
                size="medium"
                placeholder="Seleccione"
                disabled={isDisabled}
                onClear={handleClear}
                options={ordersIds.map((document) => {
                  return {
                    value: document,
                    label: document,
                  };
                })}
                onChange={handleOrderChange}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name="fechaCierre"
              rules={[{ required: false, message: "requerido" }]}
              label="Fecha Cierre"
            >
              <Select
                showSearch
                size="medium"
                placeholder="Seleccione"
                disabled={isDisabled}
                onClear={handleClear}
                options={[
                  {
                    value: "-",
                    label: "Sin Fecha",
                  },
                  ...fechasCierre
                    .filter((e) => e !== "-")
                    .map((entrega) => {
                      return {
                        value: entrega,
                        label: entrega,
                      };
                    }),
                ]}
                onChange={handleFechaCierre}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name="novedad"
              rules={[{ required: false, message: "requerido" }]}
              label="Novedad"
            >
              <Select
                showSearch
                size="medium"
                placeholder="Seleccione"
                disabled={isDisabled}
                onClear={handleClear}
                options={novedades.map((entrega) => {
                  return {
                    value: entrega,
                    label: entrega,
                  };
                })}
                onChange={handleNovedad}
              />
            </Form.Item>
          </Col>

          <Col className="gutter-row">
            <Form.Item
              name="prioridad"
              rules={[{ required: false, message: "requerido" }]}
              label="Prioridad"
            >
              <Select
                showSearch
                size="medium"
                placeholder="Seleccione"
                disabled={isDisabled}
                onClear={handleClear}
                options={prioridades.map((entrega) => {
                  return {
                    value: entrega,
                    label: entrega,
                  };
                })}
                onChange={handlePrioridad}
              />
            </Form.Item>
          </Col>

          <Col className="gutter-row">
            <Form.Item
              name="estado"
              rules={[{ required: false, message: "requerido" }]}
              label="Estado"
            >
              <Select
                showSearch
                size="medium"
                placeholder="Seleccione"
                disabled={isDisabled}
                onClear={handleClear}
                options={_statuses}
                onChange={handleStatusChange}
              />
            </Form.Item>
          </Col>

          <Col className="gutter-row">
            <Form.Item
              name="responsable"
              rules={[{ required: false, message: "requerido" }]}
              label="Responsable"
            >
              <Select
                showSearch
                size="medium"
                placeholder="Seleccione"
                disabled={isDisabled}
                onClear={handleClear}
                options={responsables.map((entrega) => {
                  return {
                    value: entrega,
                    label: entrega,
                  };
                })}
                onChange={handleResponsable}
              />
            </Form.Item>
          </Col>

          <Col className="gutter-row">
            <Form.Item
              name="cedula"
              rules={[{ required: false, message: "requerido" }]}
              label="Cedula"
            >
              <Select
                showSearch
                size="medium"
                placeholder="Seleccione"
                disabled={isDisabled}
                onClear={handleClear}
                options={documentsIds.map((document) => {
                  return {
                    value: document,
                    label: document,
                  };
                })}
                onChange={handleDocumentChange}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  );
}
